import * as _ from 'underscore';

// NOTE: This is only a subset of specs that are needed in VizPortal
export interface OpenWindowSpecs {
  width?: number;
  height?: number;
  toolbar?: number;
  resizable?: number;
  top?: number;
  left?: number;
}

/**
 * Contains window.location behavior not covered by angular's $location service.
 *
 * Why? The browser's window.location cannot be mocked. This adds one level of
 * indirection between app code and window.location so we can mock for testing.
 */

// Download the file at the uri
function download(uri: string) {
  navigateTo(uri);
}

// Open the given uri in a new window
function open(uri: string, windowName: string = '_blank', specs?: OpenWindowSpecs) {
  return window.open(uri, windowName, formatWindowOpenSpecs(specs));
}

function formatWindowOpenSpecs(specs: OpenWindowSpecs): string {
  return _.map(<_.Dictionary<any>>specs, (val: any, key: string) => (key + '=' + val)).join(',');
}

/**
 * Sets the location to the given uri
 */
function navigateTo(uri: string) {
  window.location.href = uri;
}

/**
 * Set the location of the given iframe
 */
function navigateIFrameTo(iframe: HTMLIFrameElement, uri: string) {
  iframe.contentWindow.location.href = uri;
}

function navigateToWithoutAddingToHistory(uri: string) {
  window.location.replace(uri);
}

/**
 * Cross-browser method for getting window.location.origin. Example:
 * window.location.href : 'scheme://domain:port/path?query_string#fragment_id'
 * return value         : 'scheme://domain:port'
 */
function getOrigin() {
  return window.location.protocol + '//' + window.location.host;
}

/**
 * Get everything after the host in a url.  Example:
 *
 *    window.location.href : 'scheme://domain:port/path?query_string#fragment_id'
 *    return value         : '/path?query_string#fragment_id'
 */
function getPathQueryFragment() {
  return window.location.href.split(window.location.host)[1];
}

/**
 * Get path and query components of url.  Example:
 *
 *    window.location.href : 'scheme://domain:port/path?query_string#fragment_id'
 *    return value         : '/path?query_string'
 */
function getPathQuery() {
  return getPathQueryFragment().replace(window.location.hash, '');
}

/**
 * Set everything after the host in a url. Example:
 *
 *    pathQueryFragment    : '/path?query_string#fragment_id'
 *    window.location.href : 'scheme://domain:port/path?query_string#fragment_id'
 */
function setPathQueryFragment(pathQueryFragment: string) {
  window.location.href = getOrigin() + (/^\//.test(pathQueryFragment) ? '' : '/') + pathQueryFragment;
}

function reload() {
  window.location.reload();
}

/**
 * Wrapper to stub for unit tests
 */
function getHost(){
  return window.location.host;
}

/**
 * Wrapper to stub for unit tests
 */
function getProtocol(){
  return window.location.protocol;
}

/**
 * Wrapper to stub for unit tests
 */
function getHref() {
  return window.location.href;
}

export const WindowLocationService = {
  download,
  open,
  navigateTo,
  navigateIFrameTo,
  navigateToWithoutAddingToHistory,
  getOrigin,
  getPathQueryFragment,
  getPathQuery,
  setPathQueryFragment,
  reload,
  getHost,
  getProtocol,
  getHref
};

export type WindowLocationService = typeof WindowLocationService;
